// src/api/materialsApi.ts
//
// PitchLess MATERIALS, surfaced inside CRM.
//
// ── What a "material" is ────────────────────────────────────────────────
//
// Business collateral - a proposal, a one-pager, a pitch deck - generated by
// PitchLess from a CRM record. It is called a *material* here and not an
// *artifact* because `artifact` already means something else in this codebase
// (planning/execution artifacts), and two meanings for one word in one app is
// how a rename becomes a migration.
//
// ── Where the data lives ────────────────────────────────────────────────
//
// In PitchLess. Not here. CRM stores NO row per material: no table, no
// mirror, no local copy of a version or a review. Everything below is a read
// through `/api/materials`, which signs a service-to-service call and forwards
// the user's own Central token.
//
// That has one consequence worth stating plainly, because it shapes every
// endpoint in this file: the cache tags are a cache of PITCHLESS'S ANSWERS.
// No mutation patches local state optimistically and no reducer reconstructs
// what a refine or an approval "should" have produced. Each mutation
// invalidates and re-reads, because only PitchLess knows what actually
// happened - it decides the new version id, whether the review advanced, and
// whether the workspace was charged.
//
// ── Vocabulary ──────────────────────────────────────────────────────────
//
// `status` and `reviewStatus` below mirror PitchLess's own enums verbatim. If
// PitchLess adds a state, this file is stale and the UI must degrade rather
// than crash - see `isTerminal`/`isBusy`, which are written as allow-lists of
// known-terminal states so an UNKNOWN state is treated as "still working"
// rather than silently rendered as finished.

import { baseApi } from './baseApi';

// ── The surfaces a material can be created from ─────────────────────────
//
// These strings are part of PitchLess's stored provenance, not a local enum.
// Renaming one orphans every material already filed under the old name.
export type MaterialSurface = 'estimate' | 'opportunity' | 'company';

export type MaterialFamily = 'slides' | 'document';

export type MaterialStatus =
  | 'draft'
  | 'queued'
  | 'gathering_context'
  | 'generating'
  | 'rendering'
  | 'ready'
  | 'in_review'
  | 'approved'
  | 'exported'
  | 'failed'
  | 'cancelled';

export type MaterialReviewStatus = 'drafting' | 'in-review' | 'approved' | 'needs-revision';

/** What PitchLess says THIS user may do. Never inferred client-side. */
export type MaterialRole = 'owner' | 'editor' | 'reviewer' | 'viewer' | 'system';

export interface Material {
  id: string;
  workspaceId: string;
  artifactTypeKey: string;
  family: MaterialFamily;
  title: string;
  status: MaterialStatus;
  reviewStatus: MaterialReviewStatus;
  currentVersionId: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  lastEditedByName: string | null;
  reviewerNote: string | null;
  sourceApp: string | null;
  sourceEntityType: string | null;
  sourceEntityId: string | null;
  sourceDeepLinkUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * The server's answer to "what may I do with this?".
   *
   * Authoritative. The UI must not compute permissions from `ownerUserId` or
   * a collaborator list - PitchLess applies app roles and per-artifact
   * collaboration rules this client cannot see, so a locally derived answer
   * would be wrong in exactly the cases that matter.
   */
  myRole?: MaterialRole;
}

export interface MaterialVersion {
  id: string;
  artifactId: string;
  versionNumber?: number;
  createdAt: string;
  createdByName?: string | null;
  summary?: string | null;
}

export interface MaterialReview {
  status: MaterialReviewStatus;
  reviewers?: Array<{ userId: string; name?: string | null; state?: string | null }>;
  note?: string | null;
  decisions?: Array<{
    userId: string;
    name?: string | null;
    decision: string;
    note?: string | null;
    at: string;
  }>;
}

/**
 * One entry from PitchLess's canonical artifact-type registry.
 *
 * FIELD NAMES ARE PITCHLESS'S, NOT CRM'S. `GET /v1/artifacts/types` serializes
 * `{ key, displayName, family, description, ... }`. This interface previously
 * declared `label`, which PitchLess has never sent — so it was permanently
 * `undefined` and the create dropdown fell through to rendering
 * `artifact_type_key` at the user. `description` was spelled correctly right
 * next to it and rendered fine, which is what made the cause a single wrong
 * field name rather than a missing payload.
 *
 * The registry is the ONLY source of these labels. CRM must not keep a
 * key → label map: a local copy silently goes stale the moment PitchLess adds
 * or renames a type, and the stale value would look authoritative.
 */
export interface MaterialType {
  key: string;
  /** Human-readable name, e.g. "Sales Presentation". PitchLess's own wording. */
  displayName: string;
  family?: MaterialFamily;
  description?: string | null;
}

/**
 * The registry comes back either bare or wrapped as `{ types }`; accept both.
 *
 * Exported so the dropdown and the material rows normalise identically — two
 * copies of this is how one surface ends up labelled and the other does not.
 */
export function normaliseMaterialTypes(data: unknown): MaterialType[] {
  if (Array.isArray(data)) return data as MaterialType[];
  const wrapped = (data as { types?: MaterialType[] } | undefined)?.types;
  return Array.isArray(wrapped) ? wrapped : [];
}

/**
 * The label to show for an artifact type key.
 *
 * Falls back to the key rather than to blank. A type PitchLess has added but
 * this registry response predates must still render as something the user can
 * report to support — an empty cell is worse than a raw key, which is the one
 * case where showing the key is the right answer.
 */
export function materialTypeLabel(
  types: readonly MaterialType[],
  key: string,
): string {
  return types.find((t) => t.key === key)?.displayName || key;
}

/** Whether the integration is live in this environment. Hides entry points. */
export interface MaterialsConfig {
  configured: boolean;
  surfaces: MaterialSurface[];
  /**
   * The PUBLIC PitchLess origin, for "Open in PitchLess" links.
   *
   * Null when the integration is not configured, so an unconfigured
   * environment cannot render a link to nowhere. A hostname, never a
   * credential - the server sends the origin only.
   */
  appUrl: string | null;
}

// ── State helpers ───────────────────────────────────────────────────────
//
// Allow-lists, deliberately. An UNKNOWN status - one PitchLess added after
// this file was written - must read as "still working", never as "done":
// showing a spinner for something already finished is a small annoyance,
// while showing "ready" for something still generating hands the user an
// empty document and a broken promise.

const SETTLED: readonly MaterialStatus[] = [
  'ready',
  'in_review',
  'approved',
  'exported',
  'failed',
  'cancelled',
];

export function isBusy(status: MaterialStatus): boolean {
  return !SETTLED.includes(status);
}

export function hasFailed(status: MaterialStatus): boolean {
  return status === 'failed' || status === 'cancelled';
}

/** Is there content to preview or export yet? */
export function hasContent(m: Pick<Material, 'status' | 'currentVersionId'>): boolean {
  return m.currentVersionId !== null && !isBusy(m.status);
}

// ── Errors the BFF reports ──────────────────────────────────────────────
//
// `code` is what the UI branches on; the message is for humans and may change.
export type MaterialErrorCode =
  | 'not_configured'
  /** Upstream refused the REQUEST, not the caller. Never retryable as-is. */
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'upstream_error'
  | 'timeout'
  | 'network_error'
  | 'malformed_response'
  | 'CENTRAL_SESSION_REQUIRED'
  | 'FORBIDDEN';

export function materialErrorCode(error: unknown): MaterialErrorCode | null {
  const data = (error as { data?: { code?: string } } | undefined)?.data;
  return (data?.code as MaterialErrorCode | undefined) ?? null;
}

/**
 * Is this failure the user's problem to fix, or the environment's?
 *
 * `not_configured` means no app key in this environment. Nothing the user
 * does will change it, so the UI must not offer "try again" - that is how a
 * deployment gap turns into a support ticket about a broken button.
 */
export function isEnvironmentFailure(error: unknown): boolean {
  const code = materialErrorCode(error);
  return code === 'not_configured' || code === 'upstream_error';
}

// ── Endpoints ───────────────────────────────────────────────────────────

const LIST = 'LIST' as const;

// ── The billable requests, as pure functions ────────────────────────────
//
// Both operations below cost the workspace money, and both have a rule about
// what must and must not be in the request. The builders live out here so
// those rules are unit-testable without a store.
//
// This is not stylistic. A test asserting only what a COMPONENT passes to a
// hook cannot see a field added inside `query` — and mutation testing found
// exactly that gap in the mobile twin of this file: "mint a client
// idempotency key in the slice" survived a 16-test component suite.

export interface CreateMaterialArgs {
  entityType: MaterialSurface;
  entityId: string;
  artifactTypeKey: string;
  title?: string;
  /**
   * What the material should say. Optional here; the SERVER composes one from
   * the record when it is absent, because PitchLess refuses an empty brief
   * and the user must not be shown a 400 for a field the form never asked
   * them to fill in.
   */
  instructions?: string;
  /**
   * ONLY set to deliberately create a SECOND material of a type that already
   * exists on this record. Omitted — the normal case — the server derives a
   * key from the record and the type, so a double-click is one artifact and
   * one charge.
   */
  idempotencyKey?: string;
}

export function createMaterialRequest({ entityType, entityId, ...body }: CreateMaterialArgs) {
  return {
    url: `/materials/entity/${entityType}/${entityId}`,
    method: 'POST' as const,
    body,
  };
}

export interface RefineMaterialArgs {
  id: string;
  instructions: string;
  /** Optimistic-concurrency token; PitchLess answers 409 if it is stale. */
  expectedVersionId?: string;
}

export function refineMaterialRequest({ id, ...body }: RefineMaterialArgs) {
  return {
    url: `/materials/${id}/refine`,
    method: 'POST' as const,
    body,
  };
}

export const materialsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Is the integration live here? Cheap, cached, gates every entry point. */
    getMaterialsConfig: builder.query<MaterialsConfig, void>({
      query: () => ({ url: '/materials/config' }),
      providesTags: [{ type: 'Material', id: 'CONFIG' }],
    }),

    /** PitchLess's canonical type registry. CRM must never keep its own copy. */
    getMaterialTypes: builder.query<{ types: MaterialType[] } | MaterialType[], void>({
      query: () => ({ url: '/materials/types' }),
      providesTags: [{ type: 'Material', id: 'TYPES' }],
    }),

    /** Materials created from ONE CRM record. */
    getMaterialsForEntity: builder.query<
      { artifacts: Material[] },
      { entityType: MaterialSurface; entityId: string; reviewStatus?: MaterialReviewStatus }
    >({
      query: ({ entityType, entityId, reviewStatus }) => ({
        url: `/materials/entity/${entityType}/${entityId}`,
        params: reviewStatus ? { reviewStatus } : {},
      }),
      providesTags: (result, _e, { entityType, entityId }) => [
        { type: 'Material' as const, id: `${entityType}-${entityId}` },
        ...(result?.artifacts ?? []).map((m) => ({ type: 'Material' as const, id: m.id })),
      ],
    }),

    /** Everything this workspace created from CRM - the Documents surface. */
    getMaterials: builder.query<
      { artifacts: Material[] },
      { entityType?: MaterialSurface; reviewStatus?: MaterialReviewStatus } | void
    >({
      query: (params) => ({
        url: '/materials',
        params: {
          ...(params && params.entityType ? { entityType: params.entityType } : {}),
          ...(params && params.reviewStatus ? { reviewStatus: params.reviewStatus } : {}),
        },
      }),
      providesTags: (result) => [
        { type: 'Material' as const, id: LIST },
        ...(result?.artifacts ?? []).map((m) => ({ type: 'Material' as const, id: m.id })),
      ],
    }),

    getMaterial: builder.query<Material, string>({
      query: (id) => ({ url: `/materials/${id}` }),
      providesTags: (_r, _e, id) => [{ type: 'Material', id }],
    }),

    getMaterialVersions: builder.query<{ versions: MaterialVersion[] } | MaterialVersion[], string>({
      query: (id) => ({ url: `/materials/${id}/versions` }),
      providesTags: (_r, _e, id) => [{ type: 'MaterialVersion', id }],
    }),

    getMaterialReview: builder.query<MaterialReview, string>({
      query: (id) => ({ url: `/materials/${id}/review` }),
      providesTags: (_r, _e, id) => [{ type: 'MaterialReview', id }],
    }),

    /**
     * Ask PitchLess to generate a material. BILLABLE.
     *
     * The server derives an idempotency key from the record and the type, so a
     * double-click is one artifact and one charge. `idempotencyKey` is exposed
     * here only so a caller can deliberately ask for a SECOND material of the
     * same type from the same record - which must be a deliberate act, never
     * the default.
     */
    createMaterial: builder.mutation<Material, CreateMaterialArgs>({
      query: createMaterialRequest,
      invalidatesTags: (_r, _e, { entityType, entityId }) => [
        { type: 'Material', id: `${entityType}-${entityId}` },
        { type: 'Material', id: LIST },
      ],
    }),

    /**
     * Natural-language revision. BILLABLE.
     *
     * `expectedVersionId` is what stops two people overwriting one another:
     * PitchLess answers 409 and the client re-reads. It is NOT resolved here -
     * CRM does not own the version and cannot decide whose edit wins.
     */
    refineMaterial: builder.mutation<Material, RefineMaterialArgs>({
      query: refineMaterialRequest,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Material', id },
        { type: 'MaterialVersion', id },
        { type: 'Material', id: LIST },
      ],
    }),

    submitMaterialForReview: builder.mutation<
      MaterialReview,
      { id: string; reviewerUserIds?: string[]; note?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/materials/${id}/review/submit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'MaterialReview', id },
        { type: 'Material', id },
        { type: 'Material', id: LIST },
      ],
    }),

    /** Approve or request changes. The SAME review a PitchLess reviewer sees. */
    decideMaterialReview: builder.mutation<
      MaterialReview,
      {
        id: string;
        decision: 'approve' | 'request-revision';
        note?: string;
        confidence?: 'low' | 'medium' | 'high';
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/materials/${id}/review/decision`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'MaterialReview', id },
        { type: 'Material', id },
        { type: 'Material', id: LIST },
      ],
    }),
  }),
  overrideExisting: false,
});

/**
 * The export path.
 *
 * NOT for use as an `<a href>` or an `<iframe src>`. CRM authenticates two
 * ways - a Bearer token held in Redux, and an RP `crm_sid` cookie - and a
 * plain href can only carry the cookie. Pointing an anchor here therefore
 * works on a cookie session and 401s on a Bearer session, in the same build,
 * depending on how that workspace signed in.
 *
 * Use `useMaterialFile()` instead: it fetches with the same token RTK sends,
 * turns the response into a blob, and hands back an object URL. This function
 * exists so both the hook and its tests name the path in one place.
 */
export function materialExportPath(
  id: string,
  format: 'docx' | 'pdf' | 'html',
  versionId?: string
): string {
  const q = versionId ? `?versionId=${encodeURIComponent(versionId)}` : '';
  return `/api/materials/${encodeURIComponent(id)}/export/${format}${q}`;
}

export const {
  useGetMaterialsConfigQuery,
  useGetMaterialTypesQuery,
  useGetMaterialsForEntityQuery,
  useGetMaterialsQuery,
  useGetMaterialQuery,
  useGetMaterialVersionsQuery,
  useGetMaterialReviewQuery,
  useCreateMaterialMutation,
  useRefineMaterialMutation,
  useSubmitMaterialForReviewMutation,
  useDecideMaterialReviewMutation,
} = materialsApi;
